import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, upload } from '@/lib/apiClient';
import { queryKeys } from '@/queries/keys';
import type { LibraryCategory, LibraryFile, PaginatedResponse } from '@/lib/types';

export const libraryApi = {
  categories: () => call<LibraryCategory[]>('/library/categories'),
  files: (params: { categoryId?: string; search?: string; page?: number; limit?: number } = {}) => {
    const sp = new URLSearchParams();
    if (params.categoryId) sp.set('category', params.categoryId);
    if (params.search) sp.set('search', params.search);
    if (params.page) sp.set('page', String(params.page));
    if (params.limit) sp.set('limit', String(params.limit));
    const queryStr = sp.toString();
    return call<PaginatedResponse<LibraryFile>>(`/library/files${queryStr ? `?${queryStr}` : ''}`);
  },
  createCategory: (name: string) =>
    call<LibraryCategory>('/library/categories', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  renameCategory: (id: string, name: string) =>
    call<LibraryCategory>(`/library/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  deleteCategory: (id: string) =>
    call<{ status: string }>(`/library/categories/${id}`, { method: 'DELETE' }),
  uploadFile: (categoryId: string, title: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('categoryId', categoryId);
    fd.append('title', title);
    return upload<LibraryFile>('/library/files', fd);
  },
  deleteFile: (id: string) =>
    call<{ status: string }>(`/library/files/${id}`, { method: 'DELETE' }),
};

export function useLibraryCategories() {
  return useQuery({
    queryKey: queryKeys.libraryCategories,
    queryFn: libraryApi.categories,
  });
}

export function useLibraryFiles(params: { categoryId?: string; search?: string; page?: number; limit?: number } = {}) {
  return useQuery({
    queryKey: queryKeys.libraryFiles(params.categoryId, params.search, params.page, params.limit),
    queryFn: () => libraryApi.files(params),
    enabled: Boolean(params.categoryId),
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => libraryApi.createCategory(name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.libraryCategories });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => libraryApi.deleteCategory(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.libraryCategories });
    },
  });
}

export function useUploadLibraryFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, title, file }: { categoryId: string; title: string; file: File }) =>
      libraryApi.uploadFile(categoryId, title, file),
    onSuccess: (file) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.libraryFiles(file.category_id) });
    },
  });
}

export function useDeleteLibraryFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => libraryApi.deleteFile(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['library', 'files'] });
    },
  });
}
